import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { 
  FaCheckCircle, FaCertificate, FaIndustry, FaLeaf, FaPills, 
  FaFlask, FaShieldAlt, FaUsers, FaArrowRight, FaBox, FaCogs,
  FaFileAlt, FaTruck, FaHandshake, FaGlobeAsia, FaBuilding,
  FaRocket, FaChartLine, FaDownload
} from 'react-icons/fa';
import EnquiryForm from '../components/EnquiryForm';
import { toast } from 'sonner';
import { generateProductCataloguePDF } from '../utils/pdfGenerator';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HomePage = () => {
  const [apiProducts, setApiProducts] = useState([]);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setApiProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };
  const services = [
    {
      icon: FaIndustry,
      title: 'Third Party Manufacturing',
      description: 'Complete manufacturing solutions for your brand with end-to-end support and quality assurance.'
    },
    {
      icon: FaBox,
      title: 'Private Label Manufacturing',
      description: 'Launch your own branded products with our white-label solutions and custom packaging options.'
    },
    {
      icon: FaCogs,
      title: 'Custom Product Development',
      description: 'Work with our R&D team to develop unique formulations tailored to your market needs.'
    },
    {
      icon: FaTruck,
      title: 'Bulk Manufacturing',
      description: 'Large-scale production capabilities for distributors and export clients across India.'
    },
  ];

  const products = [
    { name: 'Capsules', icon: FaPills },
    { name: 'Tablets', icon: FaPills },
    { name: 'Syrups', icon: FaFlask },
    { name: 'Sachets', icon: FaBox },
    { name: 'Effervescents', icon: FaPills },
    { name: 'Powders', icon: FaBox },
    { name: 'Oils', icon: FaFlask },
    { name: 'Ointments', icon: FaBox },
    { name: 'Cosmetics', icon: FaLeaf },
    { name: 'Herbal Soaps', icon: FaLeaf },
  ];

  const processSteps = [
    { step: '01', title: 'Raw Material', description: 'Quality sourcing' },
    { step: '02', title: 'Testing', description: 'Rigorous quality checks' },
    { step: '03', title: 'Manufacturing', description: 'GMP-compliant production' },
    { step: '04', title: 'Packaging', description: 'Professional packaging' },
    { step: '05', title: 'Quality Check', description: 'Final verification' },
  ];

  const clientTypes = [
    { icon: FaRocket, title: 'Startups', description: 'Launch your brand with low MOQ' },
    { icon: FaBuilding, title: 'Pharma Companies', description: 'Bulk manufacturing solutions' },
    { icon: FaGlobeAsia, title: 'Export Clients', description: 'International quality standards' },
    { icon: FaHandshake, title: 'Distributors', description: 'Consistent supply & support' },
  ];

  const whyChoose = [
    'High Quality Formulations',
    'Scalable Production Capacity',
    'Fast Delivery Pan India',
    'Dedicated Support Team',
    'Flexible MOQ Options',
    'Complete Regulatory Support',
  ];

  return (
    <div className='min-h-screen bg-white' data-testid='homepage'>
      {/* Hero Section */}
      <section className='relative bg-gradient-to-br from-[#0F5132] via-[#1a6b47] to-[#0F5132] text-white overflow-hidden'>
        <div className='absolute inset-0 opacity-10'>
          <div className='absolute top-0 left-0 w-96 h-96 bg-[#C9A227] rounded-full blur-3xl'></div>
          <div className='absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl'></div>
        </div>
        
        <div className='relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28'>
          <div className='text-center max-w-5xl mx-auto'>
            <div className='inline-flex items-center space-x-2 px-4 py-2 bg-[#C9A227] rounded-full mb-6'>
              <FaShieldAlt className='text-white' />
              <span className='text-white font-bold text-sm'>GMP CERTIFIED FACILITY</span>
            </div>
            
            <h1 className='text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight'>
              GMP Certified Ayurvedic &<br/>Nutraceutical Manufacturer
            </h1>
            
            <p className='text-xl sm:text-2xl text-gray-100 mb-4 font-medium'>
              Third Party Manufacturing | Private Label | Custom Formulations | Bulk Supply Across India
            </p>
            
            <p className='text-lg text-gray-200 mb-10 max-w-3xl mx-auto'>
              Reliable manufacturing partner for pharma companies, startups, and distributors. Scalable production with consistent quality.
            </p>
            
            <div className='flex flex-wrap justify-center gap-4 mb-8'>
              <a
                href='#enquiry-form'
                className='px-8 py-4 bg-[#C9A227] hover:bg-[#B8921F] text-white text-lg font-bold rounded-lg transition-all duration-200 shadow-xl hover:shadow-2xl hover:scale-105'
              >
                Get Free Quotation
              </a>
              <a
                href='https://wa.me/918264999600'
                target='_blank'
                rel='noopener noreferrer'
                className='px-8 py-4 bg-white text-[#0F5132] text-lg font-bold rounded-lg transition-all duration-200 shadow-xl hover:shadow-2xl hover:scale-105'
              >
                WhatsApp Now
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Strip Below Hero */}
      <section className='bg-[#C9A227] py-4'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
          <div className='flex flex-wrap justify-center items-center gap-8 text-white text-center'>
            <div className='flex items-center space-x-2'>
              <FaShieldAlt className='text-2xl' />
              <span className='font-bold text-lg'>GMP Certified Facility</span>
            </div>
            <div className='hidden md:block w-px h-8 bg-white/30'></div>
            <div className='flex items-center space-x-2'>
              <FaHandshake className='text-2xl' />
              <span className='font-bold text-lg'>Trusted by Growing Brands</span>
            </div>
            <div className='hidden md:block w-px h-8 bg-white/30'></div>
            <div className='flex items-center space-x-2'>
              <FaGlobeAsia className='text-2xl' />
              <span className='font-bold text-lg'>PAN India Supply</span>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Metrics */}
      <section className='py-16 bg-gray-50 border-y-2 border-[#0F5132]'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
          <div className='grid grid-cols-2 md:grid-cols-4 gap-8'>
            {[
              { number: '100+', label: 'Formulations Developed' },
              { number: '50+', label: 'Brands Served' },
              { number: 'GMP', label: 'Certified Facility' },
              { number: 'PAN', label: 'India Supply' },
            ].map((stat, index) => (
              <div key={index} className='text-center'>
                <div className='text-4xl md:text-5xl font-black text-[#0F5132] mb-2'>{stat.number}</div>
                <div className='text-gray-700 font-semibold text-sm md:text-base'>{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Power Block */}
      <section className='py-12 bg-white'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
          <div className='bg-gradient-to-r from-[#0F5132] to-[#1a6b47] rounded-2xl p-8 md:p-12 text-white'>
            <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6'>
              <div className='flex items-start space-x-3'>
                <FaCheckCircle className='text-[#C9A227] text-3xl flex-shrink-0 mt-1' />
                <div>
                  <h4 className='text-xl font-bold mb-1'>100+ Formulations</h4>
                  <p className='text-gray-200 text-sm'>Developed & Ready</p>
                </div>
              </div>
              <div className='flex items-start space-x-3'>
                <FaCheckCircle className='text-[#C9A227] text-3xl flex-shrink-0 mt-1' />
                <div>
                  <h4 className='text-xl font-bold mb-1'>50+ Brands</h4>
                  <p className='text-gray-200 text-sm'>Successfully Served</p>
                </div>
              </div>
              <div className='flex items-start space-x-3'>
                <FaCheckCircle className='text-[#C9A227] text-3xl flex-shrink-0 mt-1' />
                <div>
                  <h4 className='text-xl font-bold mb-1'>Advanced Setup</h4>
                  <p className='text-gray-200 text-sm'>Modern Manufacturing</p>
                </div>
              </div>
              <div className='flex items-start space-x-3'>
                <FaCheckCircle className='text-[#C9A227] text-3xl flex-shrink-0 mt-1' />
                <div>
                  <h4 className='text-xl font-bold mb-1'>Fast Turnaround</h4>
                  <p className='text-gray-200 text-sm'>Quick Delivery Time</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Video Section */}
      <section className='py-20 bg-white'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
          <div className='text-center mb-12'>
            <h2 className='text-4xl md:text-5xl font-black text-[#0F5132] mb-4'>Inside Our Manufacturing Facility</h2>
            <p className='text-xl text-gray-600 max-w-3xl mx-auto'>
              Explore our advanced manufacturing process, quality control systems, and scalable production capabilities.
            </p>
          </div>
          <div className='max-w-5xl mx-auto'>
            <div className='relative rounded-2xl overflow-hidden shadow-2xl border-4 border-[#0F5132]'>
              <video 
                controls 
                className='w-full'
                poster='https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=1200&q=80'
              >
                <source src='/company-video.mp4' type='video/mp4' />
                Your browser does not support the video tag.
              </video>
            </div>
          </div>
        </div>
      </section>

      {/* CTA After Video */}
      <section className='py-8 bg-gray-50'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
          <div className='flex flex-wrap justify-center gap-4'>
            <a
              href='#enquiry-form'
              className='px-10 py-4 bg-[#C9A227] hover:bg-[#B8921F] text-white text-lg font-bold rounded-lg transition-all duration-200 shadow-xl'
            >
              Get Free Quotation
            </a>
            <Link
              to='/start-your-brand'
              className='px-10 py-4 bg-[#0F5132] hover:bg-[#0D4228] text-white text-lg font-bold rounded-lg transition-all duration-200 shadow-xl'
            >
              Start Your Brand
            </Link>
            <a
              href='https://wa.me/918264999600'
              target='_blank'
              rel='noopener noreferrer'
              className='px-10 py-4 bg-[#25D366] hover:bg-[#128C7E] text-white text-lg font-bold rounded-lg transition-all duration-200 shadow-xl'
            >
              WhatsApp Now
            </a>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className='py-20 bg-gradient-to-b from-gray-50 to-white'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
          <div className='grid grid-cols-1 lg:grid-cols-2 gap-12 items-center'>
            <div>
              <h2 className='text-4xl md:text-5xl font-black text-[#0F5132] mb-8'>Trusted Manufacturing Partner</h2>
              <div className='text-lg text-gray-700 leading-relaxed space-y-4'>
                <p>
                  At <strong>Shivangel Ayurveda & Nutraceuticals</strong>, we combine traditional Ayurvedic knowledge with modern manufacturing systems.
                </p>
                <p>
                  Our <strong className='text-[#C9A227]'>GMP-certified facility</strong> ensures consistent quality, safety, and scalability for every product.
                </p>
                <p className='text-xl font-bold text-[#0F5132]'>
                  We help brands launch and grow with complete third-party manufacturing solutions.
                </p>
              </div>
            </div>
            <div>
              <img
                src='https://images.unsplash.com/photo-1528272252360-5efd274e36fb?w=800&q=80'
                alt='Ayurvedic Products Manufacturing'
                className='rounded-2xl shadow-2xl border-4 border-[#0F5132]'
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA After About */}
      <section className='py-20 bg-white'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
          <div className='text-center mb-16'>
            <h2 className='text-4xl md:text-5xl font-black text-[#0F5132] mb-4'>Our Manufacturing Services</h2>
            <p className='text-xl text-gray-600'>End-to-end solutions for your brand</p>
          </div>

          <div className='grid grid-cols-1 md:grid-cols-2 gap-8'>
            {services.map((service, index) => (
              <div
                key={index}
                className='bg-gradient-to-br from-gray-50 to-white rounded-2xl p-8 border-2 border-[#0F5132] hover:shadow-2xl transition-all duration-300 hover:-translate-y-2'
              >
                <div className='w-16 h-16 bg-[#0F5132] rounded-xl flex items-center justify-center mb-6'>
                  <service.icon className='text-[#C9A227] text-3xl' />
                </div>
                <h3 className='text-2xl font-black text-[#0F5132] mb-3'>{service.title}</h3>
                <p className='text-gray-700 leading-relaxed'>{service.description}</p>
              </div>
            ))}
          </div>

          <div className='text-center mt-12'>
            <a
              href='#enquiry-form'
              className='inline-block px-10 py-4 bg-[#C9A227] hover:bg-[#B8921F] text-white text-lg font-bold rounded-lg transition-all duration-200 shadow-xl'
            >
              Get Free Quotation
            </a>
          </div>
        </div>
      </section>

      {/* Product Range */}
      <section className='py-20 bg-gray-50'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
          <div className='text-center mb-16'>
            <h2 className='text-4xl md:text-5xl font-black text-[#0F5132] mb-4'>Complete Product Range</h2>
            <p className='text-xl text-gray-600'>140+ formulations across multiple categories</p>
          </div>

          <div className='grid grid-cols-2 md:grid-cols-5 gap-6'>
            {products.map((product, index) => (
              <div
                key={index}
                className='bg-white rounded-xl p-6 border-2 border-[#0F5132] hover:shadow-xl transition-all duration-300 hover:scale-105 text-center'
              >
                <product.icon className='text-[#0F5132] text-4xl mx-auto mb-3' />
                <h4 className='text-lg font-bold text-[#0F5132]'>{product.name}</h4>
              </div>
            ))}
          </div>

          <div className='text-center mt-12 flex flex-wrap justify-center gap-4'>
            <Link
              to='/products'
              className='inline-block px-10 py-4 bg-[#0F5132] hover:bg-[#0D4228] text-white text-lg font-bold rounded-lg transition-all duration-200 shadow-xl'
            >
              View Complete Catalogue
            </Link>
            <button
              onClick={() => {
                toast.info('Generating PDF...');
                setTimeout(() => generateProductCataloguePDF(apiProducts), 500);
              }}
              className='inline-flex items-center space-x-2 px-10 py-4 bg-[#C9A227] hover:bg-[#B8921F] text-white text-lg font-bold rounded-lg transition-all duration-200 shadow-xl'
            >
              <FaDownload />
              <span>Download PDF Catalogue</span>
            </button>
          </div>
        </div>
      </section>

      {/* Manufacturing Process */}
      <section className='py-20 bg-white'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
          <div className='text-center mb-16'>
            <h2 className='text-4xl md:text-5xl font-black text-[#0F5132] mb-4'>Our Manufacturing Process</h2>
            <p className='text-xl text-gray-600'>Quality at every step</p>
          </div>

          <div className='relative'>
            <div className='hidden md:block absolute left-0 right-0 top-1/2 h-1 bg-[#0F5132] transform -translate-y-1/2'></div>
            
            <div className='grid grid-cols-1 md:grid-cols-5 gap-8'>
              {processSteps.map((step, index) => (
                <div key={index} className='relative z-10 text-center'>
                  <div className='w-20 h-20 bg-[#0F5132] text-[#C9A227] rounded-full flex items-center justify-center text-2xl font-black mx-auto mb-4 shadow-xl'>
                    {step.step}
                  </div>
                  <h4 className='text-xl font-black text-[#0F5132] mb-2'>{step.title}</h4>
                  <p className='text-gray-600 text-sm'>{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* GMP Certification */}
      <section className='py-20 bg-gradient-to-br from-[#0F5132] to-[#1a6b47] text-white'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
          <div className='text-center max-w-4xl mx-auto'>
            <div className='w-24 h-24 bg-[#C9A227] rounded-full flex items-center justify-center mx-auto mb-8'>
              <FaCertificate className='text-white text-5xl' />
            </div>
            <h2 className='text-4xl md:text-5xl font-black mb-6'>GMP Certified Manufacturing Facility</h2>
            <p className='text-xl text-gray-100 leading-relaxed'>
              Our facility operates under <strong>Good Manufacturing Practice (GMP)</strong> standards, ensuring 
              consistent quality, safety, and regulatory compliance for all products manufactured.
            </p>
          </div>
        </div>
      </section>

      {/* Who We Serve */}
      <section className='py-20 bg-gray-50'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
          <div className='text-center mb-16'>
            <h2 className='text-4xl md:text-5xl font-black text-[#0F5132] mb-4'>Who We Serve</h2>
            <p className='text-xl text-gray-600'>Trusted by businesses across India</p>
          </div>

          <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8'>
            {clientTypes.map((client, index) => (
              <div
                key={index}
                className='bg-white rounded-xl p-8 border-2 border-[#0F5132] hover:shadow-xl transition-all duration-300 text-center'
              >
                <client.icon className='text-[#0F5132] text-5xl mx-auto mb-4' />
                <h4 className='text-xl font-black text-[#0F5132] mb-2'>{client.title}</h4>
                <p className='text-gray-600'>{client.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className='py-20 bg-white'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
          <div className='text-center mb-16'>
            <h2 className='text-4xl md:text-5xl font-black text-[#0F5132] mb-4'>Why Choose Shivangel?</h2>
          </div>

          <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto'>
            {whyChoose.map((reason, index) => (
              <div key={index} className='flex items-center space-x-3 bg-gray-50 rounded-lg p-4 border-l-4 border-[#C9A227]'>
                <FaCheckCircle className='text-[#0F5132] text-2xl flex-shrink-0' />
                <span className='text-gray-800 font-semibold'>{reason}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Strong CTA Section */}
      <section className='py-20 bg-gradient-to-r from-[#C9A227] to-[#B8921F] text-white'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center'>
          <h2 className='text-4xl md:text-5xl font-black mb-6'>
            Start Your Own Brand Today
          </h2>
          <p className='text-xl text-white/90 mb-8 max-w-3xl mx-auto'>
            Partner with India's trusted manufacturing facility. Get expert guidance, quality products, and complete support.
          </p>
          <p className='text-lg font-semibold mb-10 text-white/80'>
            ⚡ Limited slots available for new brand launches
          </p>
          <div className='flex flex-wrap justify-center gap-4'>
            <a
              href='#enquiry-form'
              className='px-10 py-4 bg-white text-[#0F5132] text-lg font-bold rounded-lg transition-all duration-200 shadow-xl hover:shadow-2xl hover:scale-105'
            >
              Get Free Quote
            </a>
            <Link
              to='/contact'
              className='px-10 py-4 bg-[#0F5132] text-white text-lg font-bold rounded-lg transition-all duration-200 shadow-xl hover:shadow-2xl hover:scale-105'
            >
              Talk to Expert
            </Link>
            <a
              href='https://wa.me/918264999600'
              target='_blank'
              rel='noopener noreferrer'
              className='px-10 py-4 bg-[#0F5132] text-white text-lg font-bold rounded-lg transition-all duration-200 shadow-xl hover:shadow-2xl hover:scale-105'
            >
              WhatsApp Now
            </a>
          </div>
        </div>
      </section>

      {/* Enquiry Form */}
      <section id='enquiry-form' className='py-20 bg-gray-50'>
        <div className='max-w-4xl mx-auto px-4 sm:px-6 lg:px-8'>
          <div className='text-center mb-12'>
            <h2 className='text-4xl md:text-5xl font-black text-[#0F5132] mb-4'>Get Free Quotation</h2>
            <p className='text-xl text-gray-600'>Fill the form below and our team will contact you within 24 hours</p>
          </div>
          <EnquiryForm title='Request Manufacturing Quote' context='manufacturing' />
        </div>
      </section>
    </div>
  );
};

export default HomePage;
